import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Stack, Button, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { organisationSchema } from '../../validation/invoiceSchema';
import InvoiceOrganisationService from '../../services/InvoiceOrganisationService';
import OrganisationForm from '../../components/invoice/OrganisationForm';

const orgService = new InvoiceOrganisationService();

const OrganisationManager = () => {
  const navigate = useNavigate();
  const [organisations, setOrganisations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(organisationSchema),
    defaultValues: {
      name: '',
      abn: '',
      email: '',
      phone: '',
      contactPerson: '',
      address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
    },
  });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await orgService.getAll();
      setOrganisations(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openCreateDialog = () => {
    setEditingId(null);
    reset({
      name: '',
      abn: '',
      email: '',
      phone: '',
      contactPerson: '',
      address: { street: '', city: '', state: '', postcode: '', country: 'Australia' },
    });
    setDialogOpen(true);
  };

  const openEditDialog = (org) => {
    setEditingId(org.id);
    reset({
      name: org.name || '',
      abn: org.abn || '',
      email: org.email || '',
      phone: org.phone || '',
      contactPerson: org.contactPerson || '',
      address: org.address || { street: '', city: '', state: '', postcode: '', country: 'Australia' },
    });
    setDialogOpen(true);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setError(null);
    try {
      if (editingId) {
        await orgService.update(editingId, data);
      } else {
        await orgService.create(data);
      }
      setDialogOpen(false);
      await loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await orgService.delete(id);
      setDeleteConfirm(null);
      await loadData();
    } catch (err) {
      setError(err.message);
      setDeleteConfirm(null);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={4}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate('/invoice')}>
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight={700}>Organisations</Typography>
              <Typography variant="body2" color="text.secondary">
                Manage your clients and payers
              </Typography>
            </Box>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
            Add Organisation
          </Button>
        </Box>

        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : organisations.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Typography color="text.secondary" mb={2}>
              No organisations yet. Add your first client or payer.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
              Add Organisation
            </Button>
          </Paper>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>ABN</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organisations.map((org) => (
                  <TableRow key={org.id}>
                    <TableCell><Typography fontWeight={600}>{org.name}</Typography></TableCell>
                    <TableCell>{org.abn || '—'}</TableCell>
                    <TableCell>{org.contactPerson || '—'}</TableCell>
                    <TableCell>{org.email || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => openEditDialog(org)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteConfirm(org)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Stack>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Organisation' : 'Add Organisation'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <OrganisationForm control={control} errors={errors} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={saving}>
            {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Add Organisation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Organisation?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm?.id)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OrganisationManager;
